import React from "react";
import { Link } from "react-router-dom";
import { Container } from "../components/ui/Container";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/Button";
import { trackTryKnoothClick } from "../lib/analytics";
import { guideArticles } from "../lib/guideData";
import { GuideIcon } from "../components/GuideLayout";

/**
 * Guide index page — overview of all guide sections.
 * Similar to https://screen.studio/guide
 */
const GuideIndex = () => {
  return (
    <div id="top" className="min-h-screen bg-[#FBFBFD]">
      <main className="pt-[140px] md:pt-[180px]">
        <Container>
          <div className="max-w-[900px] mx-auto">
            {/* ── Hero ──────────────────────────────────────────── */}
            <div className="text-center mb-16">
              <h1
                className="text-4xl md:text-6xl lg:text-[64px] font-[900] tracking-[-0.02em] leading-[1.05] text-transparent bg-clip-text pb-2"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, #31220D 57%, #666666 100%)",
                }}
              >
                knooth Guide
              </h1>
              <p className="mt-6 text-[16px] md:text-[18px] font-normal leading-[160%] text-[#666666] max-w-[600px] mx-auto">
                Everything you need to know about recording, editing, and
                exporting with knooth. Find detailed guides for every feature.
              </p>
            </div>

            {/* ── Featured guides ────────────────────────────────── */}
            <div className="mb-12">
              <h2 className="text-[14px] font-semibold tracking-[0.08em] uppercase text-[#86868B] mb-6">
                Featured Guides
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {guideArticles.slice(0, 3).map((article) => (
                  <Link
                    key={article.slug}
                    to={`/guide/${article.slug}`}
                    className="group relative rounded-2xl overflow-hidden border border-black/5 bg-gradient-to-br from-[#2B00FF]/5 to-[#AF52DE]/5 p-6 hover:border-[#2B00FF]/20 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#2B00FF]/10 flex items-center justify-center">
                        <GuideIcon icon={article.icon} className="text-[#2B00FF]" />
                      </div>
                    </div>
                    <h3 className="text-[16px] font-bold text-[#1D1D1F] group-hover:text-[#2B00FF] transition-colors">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-[13px] text-[#666666] leading-relaxed line-clamp-2">
                      {article.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            {/* ── All guides ─────────────────────────────────────── */}
            <div className="mb-16">
              <h2 className="text-[14px] font-semibold tracking-[0.08em] uppercase text-[#86868B] mb-6">
                All Guides
              </h2>
              <div className="space-y-2">
                {guideArticles.map((article, idx) => (
                  <Link
                    key={article.slug}
                    to={`/guide/${article.slug}`}
                    className="group flex items-center gap-4 p-4 rounded-2xl border border-black/5 bg-white hover:border-black/10 hover:shadow-sm transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#F5F5F7] flex items-center justify-center flex-shrink-0 group-hover:bg-[#2B00FF]/10 transition-colors">
                      <GuideIcon
                        icon={article.icon}
                        className="text-[#86868B] group-hover:text-[#2B00FF] transition-colors"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <h3 className="text-[15px] font-semibold text-[#1D1D1F] group-hover:text-[#2B00FF] transition-colors">
                          {article.title}
                        </h3>
                      </div>
                      <p className="mt-0.5 text-[13px] text-[#666666] leading-relaxed line-clamp-1">
                        {article.description}
                      </p>
                    </div>
                    <svg
                      className="w-5 h-5 text-[#86868B] group-hover:text-[#2B00FF] group-hover:translate-x-0.5 transition-all flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link>
                ))}
              </div>
            </div>

            {/* ── Help text ──────────────────────────────────────── */}
            <div className="mb-16 text-center">
              <p className="text-[14px] text-[#86868B]">
                Can't find what you're looking for?{" "}
                <Link
                  to="/support#top"
                  className="text-[#2B00FF] hover:underline"
                >
                  Contact us
                </Link>{" "}
                and we'll be happy to help.
              </p>
            </div>
          </div>

          {/* ── CTA ────────────────────────────────────────────── */}
          <div className="mt-[80px] text-center">
            <h2
              className="text-4xl md:text-5xl lg:text-[56px] font-[900] tracking-[-0.02em] leading-none text-transparent bg-clip-text"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, #31220D 57%, #666666 100%)",
              }}
            >
              Record, edit, and export —
              <br />
              all on your Mac, all in one app.
            </h2>

            <div className="mt-[40px] mb-[140px] flex flex-col items-center gap-[10px]">
              <Button
                href="https://apps.apple.com/app/knooth/id6756659300?mt=12"
                target="_blank"
                rel="noreferrer"
                data-analytics="try-knooth"
                data-location="guide-index-cta"
                onClick={() => trackTryKnoothClick("guide-index-cta")}
                className="w-[176px] h-[44px] px-6 py-[10px] rounded-full text-white font-bold text-[24px] hover:shadow-lg transition-all border-none leading-none flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(180deg, #2F00EC 57%, #C8A5FF 100%)",
                }}
              >
                Try knooth
              </Button>
              <p className="text-[14px] font-normal leading-[1.4] text-[#31220D]">
                1 week free for new subscribers.
              </p>
            </div>
          </div>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

export { GuideIndex };
